import type { Job } from "../../../../../../models";
import { ColumnContext } from "../../columns/standard-column-types";

/**
 * Props for the JobCard component used in mobile view
 */
export interface JobCardProps {
	/** The job data to display and edit */
	job: Job;
	/** Validation errors keyed by field-jobId */
	validationErrors: Record<string, string | undefined>;
	/** Column context for accessing viewmodel functions */
	columnContext: ColumnContext;
	/** Whether this card is expanded to show all fields */
	expanded: boolean;
	/** Callback when card expansion is toggled */
	onToggle: (jobId: string) => void;
	/** Callback to delete this job */
	onDelete: (jobId: string) => void;
	/** Callback to duplicate this job */
	onDuplicate: (jobId: string) => void;
	/** Whether this card is selected for bulk operations */
	selected: boolean;
	/** Callback when selection is toggled */
	onSelectToggle: (jobId: string) => void;
}

/**
 * Props for mobile field components
 * These are simpler than the MRT column props since we don't need
 * table-specific features like grouping
 */
export interface MobileFieldProps {
	/** The job being edited */
	job: Job;
	/** Field name on the job object */
	fieldName: keyof Job;
	/** Validation error for this field, if any */
	error?: string;
	/** Whether this field is required */
	required: boolean;
	/** Whether this field is disabled */
	disabled: boolean;
	/** Label to display for the field */
	label: string;
	/** Callback when value changes */
	onChange: (value: any) => void;
	/** Callback when field loses focus (for validation) */
	onBlur: () => void;
}

/**
 * Section groupings for card content
 * Fields are grouped into logical sections for better mobile UX
 */
export interface CardSection {
	title: string;
	fields: CardField[];
}

/**
 * Field definition for mobile card rendering
 */
export interface CardField {
	columnId: string;
	fieldName: keyof Job;
	label: string;
	type: "text" | "select" | "checkbox" | "date" | "file" | "phone" | "email";
	options?: any[]; // For select fields
}

/**
 * Helper function to count validation errors for a job
 */
export function countValidationErrors(validationErrors: Record<string, string | undefined>, jobId: string): number {
	return Object.entries(validationErrors).filter(([key, value]) => key.endsWith(`-${jobId}`) && value !== undefined)
		.length;
}
